import React from 'react';
import { WeezHeader, Button, SplitContainer, Message, Icon, FrameCard, ToastContext } from '@weezevent/nacre';
import Contingent from '../../../../models/contingentModel';
import LoadingCard from '../../../../components/loadingCard';
import { useForm, fetcher } from '@weezevent/weezjs-core';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ContingentForm from '../create/contingentForm';
import css from '../../chart-list.module.css';

export default function ContingentEditContainer({ organization, chartId, contingentId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [submitting, setSubmitting] = React.useState(false);

    const [form, setForm] = useForm({
        contingents: []
    });

    const [contingent, loading, sync] = Contingent.useApiModel(
        {
            organizationId: organization.id,
            chartId,
            id: contingentId,
            cache: false,
            onSuccess: contingent => {
                setForm('contingents', [{ ...contingent.object }]);
            }
        },
        [organization, chartId, contingentId]
    );

    const [contingents] = Contingent.useApiModel(
        {
            organizationId: organization.id,
            chartId: chartId
        },
        [organization, chartId]
    );

    const handleReset = React.useCallback(() => {
        sync();
    }, [sync]);

    const handleBack = React.useCallback(() => {
        history.push(`/ticket/O${organization.id}/charts/${chartId}/contingents`);
    }, [organization, chartId]);

    const handleSubmit = React.useCallback(() => {
        setSubmitting(true);
        fetcher
            .patch(`/ticket/organizations/${organization.id}/charts/${chartId}/contingents/${contingentId}`, { data: form.contingents[0] })
            .then(() => {
                toast.success(t('ticket.charts.contingents.list.edit-toast-success'));
                history.push(`/ticket/O${organization.id}/charts/${chartId}/contingents`);
            })
            .catch(e => {
                toast.error(t('ticket.charts.contingents.list.edit-toast-error'));
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [form.contingents, organization, chartId]);

    const existingContingentColors = React.useMemo(() => {
        return contingents?.map(contingent => contingent.color).filter(item => item !== form.contingents[0]?.color);
    }, [contingents, form.contingents]);

    const disableSubmit = React.useMemo(() => {
        return form.contingents[0]?.name === '';
    }, [form]);

    if (!contingent) {
        return null;
    }

    return (
        <>
            <WeezHeader
                backLink={{
                    onClick: () => handleBack()
                }}
                rightComponent={[
                    <Button
                        key="delete"
                        className={css['category-reset-btn']}
                        onClick={handleReset}
                        label={
                            <>
                                <Icon name="Reset" /> <span>{t('ticket.common.action.reset')}</span>
                            </>
                        }
                    />,
                    <Button
                        key="display"
                        className={css['category-save-btn']}
                        label={
                            <>
                                <Icon className={css['category-save-mobile-icon']} name="save" size="regular" />
                                <span className={css['category-save-mobile-label']}>{t('ticket.common.action.save')}</span>
                            </>
                        }
                        disabled={disableSubmit}
                        onClick={handleSubmit}
                        primary
                    />
                ]}
                title={contingent.name}
            />

            <SplitContainer
                additional={
                    <Message type="tips" title={t('notifications.tips_title')} mini>
                        <p>{t('notifications.category_tips')}</p>
                    </Message>
                }
            >
                <FrameCard title={t('ticket.creationEvent.stepSideBar.stepInfos')} subtitle={t('ticket.charts.newContingent.informations')}>
                    <LoadingCard loading={!contingent || loading || submitting}>
                        <ContingentForm
                            index={0}
                            organization={organization}
                            contingent={form.contingents[0]}
                            form={form}
                            setForm={setForm}
                            existingContingentColors={existingContingentColors}
                        />
                    </LoadingCard>
                </FrameCard>
            </SplitContainer>
        </>
    );
}
